import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Calendar, CalendarPlus, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { dashboardFuture } from '../api/livesDashboard';
import { getLive } from '../api/lives';
import CreateLiveDialog from './CreateLiveDialog';
import { FutureTab } from '../pages/LiveDashboardPage';

/**
 * ScheduledLivesPanel — HOTFIX UI. Single operational entry point for
 * scheduled Lives inside `Crea`. Pure composition of EXISTING pieces:
 * dashboardFuture (existing API), FutureTab (existing list + start/edit/
 * duplicate/cancel/delete actions) and CreateLiveDialog (existing form,
 * create + edit modes). Zero new logic, zero new endpoints.
 */
const ScheduledLivesPanel = ({ onBack }) => {
  const { t } = useTranslation('lives');
  const [items, setItems] = useState(null); // null = loading
  const [createOpen, setCreateOpen] = useState(false);
  const [editLive, setEditLive] = useState(null);

  const load = useCallback(async () => {
    try {
      setItems(await dashboardFuture());
    } catch (_e) {
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = async (l) => {
    try {
      setEditLive(await getLive(l.id));
    } catch (_e) {
      setEditLive(l);
    }
  };

  return (
    <div className="mx-auto max-w-[860px] px-4 pt-6 pb-24" data-testid="scheduled-lives-panel">
      <button
        onClick={onBack}
        className="mb-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        data-testid="scheduled-lives-back-btn"
      >
        <ArrowLeft className="h-4 w-4" /> {t('scheduled_panel.back')}
      </button>
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-xl font-bold">
              <Calendar className="h-5 w-5 gold-text" /> {t('scheduled_panel.title')}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('scheduled_panel.subtitle')}</p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="gold-gradient shrink-0 text-sm font-semibold"
            data-testid="scheduled-lives-new-btn"
          >
            <CalendarPlus className="mr-1.5 h-4 w-4" /> {t('scheduled_panel.new_cta')}
          </Button>
        </div>

        <div className="mt-5">
          {items === null ? (
            <div className="flex justify-center py-12" data-testid="scheduled-lives-loading">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center" data-testid="scheduled-lives-empty">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
                <Calendar className="h-7 w-7 gold-text" strokeWidth={1.6} />
              </div>
              <p className="text-sm font-semibold">{t('scheduled_panel.empty')}</p>
              <button
                onClick={() => setCreateOpen(true)}
                className="mt-3 text-sm font-semibold gold-text hover:underline"
                data-testid="scheduled-lives-empty-cta"
              >
                {t('scheduled_panel.empty_cta')}
              </button>
            </div>
          ) : (
            <FutureTab items={items} onRefresh={load} t={t} onEdit={openEdit} />
          )}
        </div>
      </div>

      {/* SAME existing dialog, create + edit modes — no duplicated form */}
      <CreateLiveDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={() => load()} />
      <CreateLiveDialog
        open={!!editLive}
        onOpenChange={(o) => { if (!o) setEditLive(null); }}
        editLive={editLive}
        onUpdated={() => { setEditLive(null); load(); }}
      />
    </div>
  );
};

export default ScheduledLivesPanel;
